import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Check, TrendingUp, Info, Scale } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-media-query';
import type { ContainerSize } from '../order/ContainerSizeSelector';

interface ContainerComparatorProps {
  containers: ContainerSize[];
  maxComparisons?: number;
  onSelect: (container: ContainerSize) => void;
  selectedContainerId?: string | null;
  materialType?: string;
  className?: string;
}

// Visual size indicator component with relative scaling
const VisualSizeIndicator: React.FC<{
  dimensions: ContainerSize['dimensions'];
  size: string;
  variant?: 'comparison' | 'standalone';
}> = ({ dimensions, size, variant = 'comparison' }) => {
  // Scale based on actual container size (10Y, 20Y, 30Y, 40Y)
  const sizeScale = {
    '10Y': 1.0,
    '20Y': 1.4,
    '30Y': 1.7,
    '40Y': 2.0
  };

  const scale = sizeScale[size as keyof typeof sizeScale] || 1.0;

  return (
    <div className={cn(
      "relative flex items-center justify-center",
      variant === 'comparison' ? 'h-24 w-full' : 'h-32 w-full'
    )}>
      <div
        className="bg-primary-600/10 border-2 border-primary-600/30 rounded-md flex items-center justify-center transition-transform duration-300 hover:scale-105"
        style={{
          width: `${scale * 60}px`,
          height: `${scale * 45}px`,
          transform: `perspective(400px) rotateX(15deg) rotateY(-15deg)`,
        }}
      >
        <span className="text-xs font-medium text-primary-700">
          {dimensions.length}&apos;×{dimensions.height}&apos;
        </span>
      </div>
    </div>
  );
};

// Desktop comparison table
const DesktopComparison: React.FC<{
  containers: ContainerSize[];
  selectedContainerId?: string | null;
  onSelect: (container: ContainerSize) => void;
}> = ({ containers, selectedContainerId, onSelect }) => {
  return (
    <div className="w-full space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Feature</TableHead>
            {containers.map(container => (
              <TableHead key={container.id} className="text-center">
                <div className="space-y-2">
                  <div className="font-semibold text-base">{container.size}</div>
                  <div className="text-sm text-gray-600">{container.description}</div>
                  {container.socialProof && (
                    <Badge variant="secondary" className="gap-1 text-xs">
                      <TrendingUp className="w-3 h-3" />
                      {container.socialProof.value}
                    </Badge>
                  )}
                  {container.recommended && !container.socialProof && (
                    <Badge variant="secondary" className="gap-1 text-xs">
                      <TrendingUp className="w-3 h-3" />
                      Most Popular
                    </Badge>
                  )}
                  {selectedContainerId === container.id && (
                    <Badge className="gap-1 text-xs bg-primary-600">
                      <Check className="w-3 h-3" />
                      Current Selection
                    </Badge>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="font-medium">Visual Size</TableCell>
            {containers.map(container => (
              <TableCell key={container.id}>
                <VisualSizeIndicator
                  dimensions={container.dimensions}
                  size={container.size}
                  variant="comparison"
                />
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell className="font-medium">Capacity</TableCell>
            {containers.map(container => (
              <TableCell key={container.id} className="text-center">
                <div className="space-y-1">
                  <div className="font-medium">{container.capacity.cubicYards} cubic yards</div>
                  <div className="text-xs text-gray-600">{container.capacity.tons} tons</div>
                </div>
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell className="font-medium">Dimensions</TableCell>
            {containers.map(container => (
              <TableCell key={container.id} className="text-center text-sm">
                {container.dimensions.length}&apos;L × {container.dimensions.width}&apos;W × {container.dimensions.height}&apos;H
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell className="font-medium">Equivalent Truck Loads</TableCell>
            {containers.map(container => (
              <TableCell key={container.id} className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Scale className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">{container.capacity.truckLoads}</span>
                </div>
              </TableCell>
            ))}
          </TableRow>
          {containers.some(c => c.price) && (
            <TableRow>
              <TableCell className="font-medium">Starting Price</TableCell>
              {containers.map(container => (
                <TableCell key={container.id} className="text-center">
                  {container.price ? (
                    <span className="font-semibold text-lg">{container.price}</span>
                  ) : (
                    <span className="text-gray-500 text-sm">Call for quote</span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          )}
          <TableRow>
            <TableCell />
            {containers.map(container => (
              <TableCell key={container.id} className="text-center">
                <Button
                  onClick={() => onSelect(container)}
                  className="w-full"
                  size="sm"
                  variant={selectedContainerId === container.id ? "default" : "outline"}
                >
                  {selectedContainerId === container.id ? "Selected" : "Select This Size"}
                </Button>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

// Mobile comparison carousel
const MobileComparison: React.FC<{
  containers: ContainerSize[];
  selectedContainerId?: string | null;
  onSelect: (container: ContainerSize) => void;
}> = ({ containers, selectedContainerId, onSelect }) => {
  return (
    <Carousel className="w-full">
      <CarouselContent className="-ml-2 md:-ml-4">
        {containers.map(container => (
          <CarouselItem key={container.id} className="pl-2 md:pl-4 basis-4/5">
            <Card className={cn(
              "h-full transition-all duration-200",
              selectedContainerId === container.id && "ring-2 ring-primary-600 shadow-lg"
            )}>
              <CardHeader>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{container.size}</h3>
                    {selectedContainerId === container.id && (
                      <Badge className="bg-primary-600">
                        <Check className="w-3 h-3 mr-1" />
                        Selected
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{container.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {container.socialProof && (
                      <Badge variant="secondary" className="gap-1 text-xs">
                        <TrendingUp className="w-3 h-3" />
                        {container.socialProof.value}
                      </Badge>
                    )}
                    {container.recommended && !container.socialProof && (
                      <Badge variant="secondary" className="gap-1 text-xs">
                        <TrendingUp className="w-3 h-3" />
                        Most Popular
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <VisualSizeIndicator
                  dimensions={container.dimensions}
                  size={container.size}
                  variant="standalone"
                />
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Capacity</span>
                    <span className="font-medium">{container.capacity.cubicYards} cubic yards</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Holds</span>
                    <span className="font-medium">{container.capacity.tons} tons</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Dimensions</span>
                    <span className="font-medium text-sm">
                      {container.dimensions.length}&apos;×{container.dimensions.width}&apos;×{container.dimensions.height}&apos;
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Truck Loads</span>
                    <div className="flex items-center gap-1">
                      <Scale className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">{container.capacity.truckLoads}</span>
                    </div>
                  </div>
                  {container.price && (
                    <div className="flex justify-between items-center border-t pt-3">
                      <span className="text-sm text-gray-600">Starting at</span>
                      <span className="font-semibold text-lg">{container.price}</span>
                    </div>
                  )}
                  <div className="border-t pt-3">
                    <p className="text-xs text-gray-600 mb-2">Perfect for:</p>
                    <div className="flex flex-wrap gap-1">
                      {container.commonUses.map((use, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {use}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  onClick={() => onSelect(container)}
                  className="w-full"
                  variant={selectedContainerId === container.id ? "default" : "outline"}
                >
                  {selectedContainerId === container.id ? "Selected" : "Select This Size"}
                </Button>
              </CardFooter>
            </Card>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="left-0" />
      <CarouselNext className="right-0" />
    </Carousel>
  );
};

// Main comparator component
export const ContainerComparator: React.FC<ContainerComparatorProps> = ({
  containers,
  maxComparisons = 3,
  onSelect,
  selectedContainerId,
  materialType,
  className
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Show all available containers for comparison (up to maxComparisons)
  const containersToCompare = containers.slice(0, Math.min(maxComparisons, containers.length));

  return (
    <div className={cn("w-full space-y-6", className)}>
      {/* Header with comparison count */}
      <div className="flex flex-col gap-2">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Compare Container Sizes</h2>
          <p className="text-gray-600 mt-1">
            Side-by-side comparison to help you choose the right size{materialType ? ` for ${materialType}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Info className="w-3 h-3" />
            Comparing {containersToCompare.length} containers
          </Badge>
          {selectedContainerId && (
            <Badge className="gap-1 bg-primary-600">
              <Check className="w-3 h-3" />
              Current: {containers.find(c => c.id === selectedContainerId)?.size}
            </Badge>
          )}
        </div>
      </div>

      {/* Comparison view */}
      {isDesktop ? (
        <DesktopComparison
          containers={containersToCompare}
          selectedContainerId={selectedContainerId}
          onSelect={onSelect}
        />
      ) : (
        <MobileComparison
          containers={containersToCompare}
          selectedContainerId={selectedContainerId}
          onSelect={onSelect}
        />
      )}
    </div>
  );
};

export default ContainerComparator;